import type { PlStatementRowView } from '@/api/map/plStatement'
import type { Role } from '@/data/types'
import type {
  SurveillanceCurrency,
  SurveillanceGraphFilter,
} from '@/features/surveillance/types'

export type MemberStatementStatus =
  | 'Rollback'
  | 'Invalid'
  | 'P&L Rollback'
  | 'Invalidate P&L'
  | '-'

export type MemberStatementMode = 'player-pnl' | 'pnl'

/** Re-export — transfer labels live next to `plStatementSubGame` (not asSport). */
export { isStatementTransferSubGame } from '@/api/map/plStatement'

/** Lab `totalNetAmount` seat keys — camelCase(level + 'NetAmt'). */
export type LabTotalNetSeatKey =
  | 'masterNetAmt'
  | 'superMasterNetAmt'
  | 'doubleMasterNetAmt'
  | 'tripleMasterNetAmt'
  | 'companyMasterNetAmt'

export type LabTotalNetAmountSeats = Record<LabTotalNetSeatKey, number>

export type GetDownlineTotalNetAmountResponse = {
  value: boolean
  data?: {
    netAmount?: number
    totalNetAmount?: Partial<LabTotalNetAmountSeats>
  }
}

const SEAT_NET_KEY: Partial<Record<Role, LabTotalNetSeatKey>> = {
  master: 'masterNetAmt',
  'super-master': 'superMasterNetAmt',
  'double-master': 'doubleMasterNetAmt',
  'triple-master': 'tripleMasterNetAmt',
  'company-master': 'companyMasterNetAmt',
}

/** Account Statement Status — Invalid Bet + Invalidate P&L (Lab / CM list UX). */
export function memberStatementStatus(row: {
  isInvalid?: boolean
  isRollback?: boolean
  isPNLInvalid?: boolean
  isPNLRollBack?: boolean
}): MemberStatementStatus {
  // Bet-level first (Lab Invalid / Rollback), then P&L void flags.
  if (row.isRollback) return 'Rollback'
  if (row.isInvalid) return 'Invalid'
  if (row.isPNLRollBack) return 'P&L Rollback'
  if (row.isPNLInvalid) return 'Invalidate P&L'
  return '-'
}

/** Event cell prefixes for Invalid Bet + Invalidate P&L rows. */
export function memberStatementEventLabel(row: {
  isInvalid?: boolean
  isRollback?: boolean
  isPNLInvalid?: boolean
  isPNLRollBack?: boolean
  eventName?: string
  marketName?: string
}): string {
  const prefix = row.isRollback
    ? 'Rollback bets-'
    : row.isInvalid
      ? 'Invalid bets-'
      : row.isPNLRollBack
        ? 'P&L Rollback-'
        : row.isPNLInvalid
          ? 'Invalidate P&L-'
          : ''
  const event = String(row.eventName ?? '').trim()
  const match = String(row.marketName ?? '').trim()
  const body = match ? `${event} - ${match}` : event
  return `${prefix}${body}`.trim()
}

/**
 * Lab list Graph gate (Company Master): SportsBook Match Odds-style rows.
 * Fancy / Bookmaker / casino never show the chart icon on the list.
 */
export function canShowMemberStatementGraph(row: Pick<PlStatementRowView, 'game'>): boolean {
  return row.game === 'SportsBook'
}

/**
 * Lab Total Net Share is player-side. `fromDownlineAccountsData` netPnl is
 * book-inverted (−Σ memberPnl) when using the page fallback — negate once.
 */
export function memberStatementTotalNetShare(netPnl: number | undefined, pageMemberPnlSum: number): number {
  if (typeof netPnl === 'number' && Number.isFinite(netPnl)) return -netPnl
  return pageMemberPnlSum
}

export function labTotalNetSeatKey(role: Role): LabTotalNetSeatKey | null {
  return SEAT_NET_KEY[role] ?? null
}

/**
 * Lab row Net Amt:
 * - Player P&L Statement → player `memberPnl`
 * - P&L Statement on an agent seat → that seat’s split share
 * - P&L Statement on a player → still player `memberPnl`
 */
export function memberStatementNetAmt(
  row: Pick<PlStatementRowView, 'memberPnl' | 'split'>,
  mode: MemberStatementMode,
  accountRole: Role,
): number {
  if (mode === 'player-pnl' || accountRole === 'player') return row.memberPnl
  const seat = row.split?.[accountRole]
  return typeof seat === 'number' && Number.isFinite(seat) ? seat : 0
}

/**
 * Lab Total Net Share from `getDownlineTotalNetAmount`:
 * - Player P&L, or viewed player → `data.netAmount`
 * - P&L Statement on agent seat → `data.totalNetAmount[seatNetAmt]`
 */
export function memberStatementTotalNetShareFromApi(
  response: GetDownlineTotalNetAmountResponse | null | undefined,
  mode: MemberStatementMode,
  accountRole: Role,
  fallback: number,
): number {
  if (!response?.value || !response.data) return fallback
  if (mode === 'pnl' && accountRole !== 'player') {
    const key = labTotalNetSeatKey(accountRole)
    const seat = key ? response.data.totalNetAmount?.[key] : undefined
    if (typeof seat === 'number' && Number.isFinite(seat)) return seat
  }
  const net = response.data.netAmount
  if (typeof net === 'number' && Number.isFinite(net)) return net
  return fallback
}

/** Archive surveillance filter from a list row (Lab Graph column). */
export function memberStatementGraphFilter(
  row: Pick<PlStatementRowView, 'sport' | 'eventId' | 'marketId' | 'currencyType'>,
  playerUsername: string,
): SurveillanceGraphFilter | null {
  const eventType = String(row.sport ?? '').trim()
  const eventId = String(row.eventId ?? '').trim()
  const marketId = String(row.marketId ?? '').trim()
  if (!eventType || !eventId || !marketId) return null

  const currencyRaw = String(row.currencyType ?? 'All').trim()
  const currencyType = (
    currencyRaw === 'INR' ||
    currencyRaw === 'POINTS' ||
    currencyRaw === 'POINT@1' ||
    currencyRaw === 'All'
      ? currencyRaw
      : 'All'
  ) as SurveillanceCurrency

  return {
    mode: 'archive',
    eventType,
    eventId,
    marketId,
    playerNames: playerUsername.trim() ? [playerUsername.trim()] : [],
    fromStake: null,
    toStake: null,
    currencyType,
  }
}

/** datetime-local value from a Date (local wall clock). */
export function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function isoDatetimeOffset(daysBack: number, endOfDay = false): string {
  const d = new Date()
  d.setDate(d.getDate() - daysBack)
  if (endOfDay) {
    d.setHours(23, 59, 0, 0)
  } else {
    d.setHours(0, 0, 0, 0)
  }
  return toDatetimeLocalValue(d)
}
